import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewChild
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { IFormsProvider, ResourceData, ResourceState, TimestampHelper } from '@lfw/roesberg.liveforms.core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { WebcamImage, WebcamModule } from 'ngx-webcam';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { MB } from '../../../../utils/constants';
import { extractXmlProps, xmlElementToString } from '../../../../utils/xmlParser';
import { LiveFormsTranslateLoaderService } from '../../../services/live-forms-translate-loader.service';
import { InteractiveItemComponent } from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';
import { IMAGE_COMPRESSION_SCALE_STEP, DEFAULT_MAX_IMAGE_UPLOAD_SIZE_BYTES } from '../../../../utils/constants';

interface GalleryItem {
  resourceData: ResourceData;
  src?: string;
  loaded: boolean;
}

interface PhotoItemProperties {
  text: string;
  userName: string;
  addedWhileProcessing: string;
  resultValue: string;
  resultUser: string;
  resultTime: string;
  style: string;
  prefix: string;
}

const ALLOWED_FILE_FORMATS = ['png', 'jpeg'];

@Component({
  selector: 'lfe-photo-item-form',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatIcon, WebcamModule],
  templateUrl: './photo-item-form.component.html',
  standalone: true,
  styleUrl: './photo-item-form.component.css'
})
export class PhotoItemFormComponent
  extends InteractiveItemComponent<PhotoItemProperties>
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement> | undefined;

  private readonly webcamTrigger = new Subject<void>();

  resourceDataChanged = output<{ photoItemId: string; resourceData: ResourceData[] }>();

  formsProvider = inject(IFormsProvider);
  deviceDetector = inject(DeviceDetectorService);
  translateService = inject(LiveFormsTranslateLoaderService);
  formControl: FormControl<string | null> | undefined;

  galleryItems = signal<GalleryItem[]>([]);
  previewItem = signal<GalleryItem | undefined>(undefined);
  orderId = input.required<string>();
  loadedGalleryItems = signal<GalleryItem[]>([]);
  erroredGalleryItems = signal<GalleryItem[]>([]);
  initialValue = '';
  itemsLoadingCount = signal(0);
  showPhotoMenu = signal(false);
  showWebcamDialog = signal(false);
  showWebcam = signal(true);
  capturedWebcamImage = signal<WebcamImage | null>(null);
  webcamErrorMessage = signal<string | null>(null);
  uploadErrorMessage = signal<string | null>(null);
  isGalleryFirstLoaded = signal(false);

  isCommentPhotoItem = computed(() => this.properties.addedWhileProcessing === 'True');
  isDesktopDevice = computed(() => this.deviceDetector.isDesktop());

  displayGalleryItems = computed(() =>
    this.galleryItems().filter((item) =>
      [ResourceState.Unchanged, ResourceState.New, ResourceState.Uploaded, ResourceState.PendingUpload].includes(
        item.resourceData.state
      )
    )
  );

  innerValue = computed(() => {
    return [this.orderId(), ...this.displayGalleryItems().map((item) => `${item.resourceData.fileId}`)]
      .filter(Boolean)
      .join('|');
  });

  userNameTrimmed = computed(() => this.properties.userName.trim());

  private readonly maxUploadSizeBytes = computed(() => this.settings().maxImageUploadSize * MB);

  properties: InteractiveItemProperties & PhotoItemProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    text: '',
    userName: '',
    addedWhileProcessing: 'False',
    style: 'Normal',
    prefix: ''
  };

  override ngOnInit(): void {
    super.ngOnInit();
    this.formControlId = this.properties.customId || this.properties.id;
    this.initialValue = this.properties.resultValue || '';
    this.formControl = new FormControl(this.initialValue);
    this.form().addControl(this.formControlId, this.formControl);
  }

  ngAfterViewInit(): void {
    this.loadImages();
  }

  override ngOnDestroy(): void {
    this.webcamTrigger.complete();
    super.ngOnDestroy();
  }

  override extractLocalProperties(xml: Element): InteractiveItemProperties & PhotoItemProperties {
    const props = extractXmlProps(xml);

    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id: props['ID'],
      customId: props['CustomID'],
      activationRule: props['ActivationRule'] || '',
      resultValue: props['ResultValue'] || '',
      resultUser: props['ResultUser'] || '',
      resultTime: props['ResultTime'] || '',
      text: props['Text'] || '',
      userName: props['UserName'] || '',
      addedWhileProcessing: props['AddedWhileProcessing'] || '',
      style: props['Style'] || 'Normal',
      prefix: props['Prefix'] || '',
      isGenericRepeatItem
    };
  }

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `photo_${this.currentId()}`);

  override getXML(): string {
    const element = this.getXmlCloneElement();

    if (this.innerValue() !== this.initialValue) {
      this.setResultChildren(element);
    }

    return xmlElementToString(element) || '';
  }

  onPhotoMenuClick(e: Event): void {
    e.preventDefault();
    this.showPhotoMenu.set(!this.showPhotoMenu());
  }

  onSelectFileClick(e: Event): void {
    e.preventDefault();
    this.showPhotoMenu.set(false);
    this.fileInput?.nativeElement.click();
  }

  onPhotoOpenClick(e: Event): void {
    e.preventDefault();
    this.fileInput?.nativeElement.click();
  }

  async onFileChange(event: any): Promise<void> {
    const [file]: [Blob] = event.target.files;

    // Handle case when user cancels file selection
    if (!file) {
      return;
    }

    const [_, fileFormat] = file.type.split('/');

    if (!ALLOWED_FILE_FORMATS.includes(fileFormat)) {
      this.uploadErrorMessage.set(
        this.translateService.instant('liveforms-editor.photo-item.error.unsupported-format')
      );
      return;
    }

    let processedFile: Blob;
    try {
      processedFile = await this.compressImageToLimit(file, file.type);
    } catch {
      this.uploadErrorMessage.set(
        this.translateService.instant('liveforms-editor.photo-item.error.compression-failed')
      );
      return;
    }

    const resource: ResourceData = {
      data: processedFile,
      fileId: `${this.properties.prefix || ''}${TimestampHelper.getTimestamp()}.${fileFormat}`,
      state: ResourceState.New,
      saveId: '',
      orderId: this.orderId()
    };

    const src = await this.blobToBase64(processedFile);
    await this.formsProvider.saveFormResource(resource);

    const newGallery = [...this.galleryItems(), { resourceData: resource, src, loaded: true }];
    this.galleryItems.set(newGallery);
    this.resourceDataChanged.emit({
      photoItemId: this.currentId(),
      resourceData: newGallery.map((g) => g.resourceData)
    });
    this.refreshStoreData();
  }

  private refreshStoreData(): void {
    this.properties.resultValue = this.innerValue();
    this.properties.resultUser = this.user().displayName;
    this.properties.resultTime = new Date().toISOString();
    this.formControl?.markAsDirty();
  }

  protected async compressImageToLimit(blob: Blob, mimeType: string, limit = DEFAULT_MAX_IMAGE_UPLOAD_SIZE_BYTES): Promise<Blob> {
    if (blob.size <= limit) {
      return blob;
    }

    const bitmap = await createImageBitmap(blob);
    try {
      let width = bitmap.width;
      let height = bitmap.height;
      let compressed = blob;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;

      while (compressed.size > limit) {
        width = Math.floor(width * IMAGE_COMPRESSION_SCALE_STEP);
        height = Math.floor(height * IMAGE_COMPRESSION_SCALE_STEP);

        if (width < 1 || height < 1) {
          throw new Error('Image cannot be compressed to fit within the size limit');
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(bitmap, 0, 0, width, height);

        compressed = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((b) => {
            if (b) resolve(b);
            else reject(new Error('Canvas toBlob failed'));
          }, mimeType);
        });
      }

      return compressed;
    } finally {
      bitmap.close();
    }
  }

  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const cleanup = () => {
        reader.onload = null;
        reader.onerror = null;
      };
      reader.onload = () => {
        cleanup();
        resolve(reader.result as string);
      };
      reader.onerror = (error) => {
        cleanup();
        reject(error);
      };
      reader.readAsDataURL(blob);
    });
  }

  async loadImages(): Promise<void> {
    const loadedFileIds = this.properties.resultValue.split('|').filter((item) => item.includes('.'));
    this.itemsLoadingCount.set(loadedFileIds.length);
    this.isGalleryFirstLoaded.set(false);

    const resources = loadedFileIds.map((fileId) => {
      const resource: ResourceData = {
        data: new Blob(),
        fileId: fileId,
        state: ResourceState.Unchanged,
        saveId: '',
        orderId: this.orderId()
      };
      return resource;
    });

    const galleryItems = await Promise.all(
      resources.map(async (item) => {
        const galleryItem: GalleryItem = { resourceData: item, loaded: false };
        try {
          galleryItem.resourceData.data = await firstValueFrom(this.formsProvider.loadFormResource(item.fileId));
          if (galleryItem.resourceData.data) {
            const base64 = await this.blobToBase64(galleryItem.resourceData.data);
            galleryItem.src = base64;
            galleryItem.loaded = true;
          }
        } catch (error) {
          console.error('Failed to load image:', item.fileId, error);
        }
        return galleryItem;
      })
    );

    const newGallery = [...this.galleryItems(), ...galleryItems];
    this.galleryItems.set(newGallery);
    this.isGalleryFirstLoaded.set(true);
    this.resourceDataChanged.emit({
      photoItemId: this.currentId(),
      resourceData: newGallery.map((g) => g.resourceData)
    });
  }

  setItemAsLoaded(item: GalleryItem): void {
    this.loadedGalleryItems.update((values) => {
      values.push(item);
      return values;
    });
  }

  setItemAsErrored(item: GalleryItem): void {
    this.erroredGalleryItems.update((values) => {
      values.push(item);
      return values;
    });
  }

  isLoaded(item: GalleryItem) {
    return this.loadedGalleryItems().find((i) => i === item);
  }

  isErrored(item: GalleryItem) {
    return this.erroredGalleryItems().find((i) => i === item);
  }

  onThumbnailClick(e: Event, item: GalleryItem): void {
    e.preventDefault();
    this.previewItem.set(item);
  }

  onDeletePhotoClick(): void {
    const item = this.previewItem();
    if (!item) return;

    const state = item.resourceData.state;

    switch (state) {
      case ResourceState.Deleted:
        console.error('Cannot delete a deleted photo');
        break;
      case ResourceState.Unchanged:
      case ResourceState.New:
      case ResourceState.Uploaded:
      case ResourceState.PendingUpload: {
        const newGallery = this.galleryItems().map((galleryItem) => {
          if (galleryItem.resourceData.fileId === item.resourceData.fileId) {
            galleryItem.resourceData.state = ResourceState.Deleted;
          }
          return galleryItem;
        });
        this.galleryItems.set(newGallery);
        this.resourceDataChanged.emit({
          photoItemId: this.currentId(),
          resourceData: newGallery.map((g) => g.resourceData)
        });
        this.refreshStoreData();
        break;
      }
      default: {
        console.warn(`Unhandled ResourceState for delete photo click: ${state}`);
      }
    }
    this.previewItem.set(undefined);
  }

  closePreview(): void {
    this.previewItem.set(undefined);
  }

  get webcamTriggerObservable(): Observable<void> {
    return this.webcamTrigger.asObservable();
  }

  onCaptureClick(e: Event): void {
    e.preventDefault();
    this.showPhotoMenu.set(false);
    this.showWebcamDialog.set(true);
    this.showWebcam.set(true);
    this.capturedWebcamImage.set(null);
    this.webcamErrorMessage.set(null);
  }

  triggerWebcamCapture(): void {
    this.webcamTrigger.next();
  }

  handleWebcamImage(webcamImage: WebcamImage): void {
    this.capturedWebcamImage.set(webcamImage);
    this.showWebcam.set(false);
  }

  handleWebcamInitError(error: any): void {
    this.webcamErrorMessage.set(this.translateService.instant('liveforms-editor.photo-item.webcam.error'));
    console.error('Webcam init error:', error);
  }

  retakeWebcamPhoto(): void {
    this.capturedWebcamImage.set(null);
    this.showWebcam.set(true);
    this.webcamErrorMessage.set(null);
  }

  async confirmWebcamPhoto(): Promise<void> {
    const webcamImage = this.capturedWebcamImage();
    if (!webcamImage) {
      return;
    }

    const base64Data = webcamImage.imageAsDataUrl;
    const blob = await this.base64ToBlob(base64Data);

    let processedBlob: Blob;
    try {
      processedBlob = await this.compressImageToLimit(blob, 'image/png');
    } catch {
      this.uploadErrorMessage.set(
        this.translateService.instant('liveforms-editor.photo-item.error.compression-failed')
      );
      return;
    }

    const resource = new ResourceData();
    resource.data = processedBlob;
    resource.fileId = `${this.properties.prefix || ''}${TimestampHelper.getTimestamp()}.png`;
    resource.state = ResourceState.New;
    resource.orderId = this.orderId();
    resource.saveId = '';

    await this.formsProvider.saveFormResource(resource);

    const src = processedBlob === blob ? base64Data : await this.blobToBase64(processedBlob);
    const newGallery = [...this.galleryItems(), { resourceData: resource, src, loaded: true }];
    this.galleryItems.set(newGallery);
    this.resourceDataChanged.emit({
      photoItemId: this.currentId(),
      resourceData: newGallery.map((g) => g.resourceData)
    });

    this.refreshStoreData();

    this.closeWebcamDialog();
  }

  closeWebcamDialog(): void {
    this.showWebcamDialog.set(false);
    this.capturedWebcamImage.set(null);
    this.showWebcam.set(true);
    this.webcamErrorMessage.set(null);
  }

  private async base64ToBlob(base64: string): Promise<Blob> {
    const response = await fetch(base64);
    return response.blob();
  }

  protected readonly Array = Array;
}
